<script>
import { debounce } from 'lodash'
import ElTableDraggable from 'element-ui-el-table-draggable'
import lazyLoadComponent from '@router/lazyload-component'
import { getHotelGoodsList, deleteHotelGoods, updateHotelGoods, sortHotelGoods } from '@/src/apis/hotelGoods'
import { getHotelGoodsTags } from '@/src/apis/hotelGoodsTag'
import { messageBoxDelete } from '@utils/message-box'
import { messageDelete } from '@utils/message'

export default {
  components: {
    ElTableDraggable,
    PriceSubTable: () => lazyLoadComponent(import('./components/PriceSubTable.vue')),
    GoodsTagManage: () => lazyLoadComponent(import('./components/GoodsTagManage.vue')),
  },
  data() {
    return {
      tableData: [],
      listTotal: 0,
      useServerPagination: false,
      tagList: [],
      categoryMap: {},
      categoryOptions: [],
      selectedIds: [],
      loading: false,
      sortMode: false,
      sortDirty: false,
      sortSaving: false,
      tagManageVisible: false,
      currencySymbol: '$',
      pagination: {
        page: 1,
        pageSize: 15,
      },
      filterData: {
        search: '',
        status: '',
        tagIds: [],
        categoryIds: [],
      },
    }
  },
  computed: {
    canDrag() {
      return this.sortMode
    },
    totalItems() {
      return this.useServerPagination ? this.listTotal : this.tableData.length
    },
    pagedTableData() {
      if (this.sortMode) return this.tableData
      if (this.useServerPagination) return this.tableData
      const { page, pageSize } = this.pagination
      return this.tableData.slice((page - 1) * pageSize, page * pageSize)
    },
  },
  async created() {
    this.tryToFetchList()
    this.tryToFetchTagList()
    try {
      const categories = await this.$store.dispatch('product/fetchServiceCategory')
      const map = {}
      const options = Array.isArray(categories) ? categories : []
      options.forEach((cat) => {
        const key = String(cat.id)
        map[key] = cat.categoryTitle || cat.title || key
      })
      this.categoryMap = map
      this.categoryOptions = options.map((cat) => ({
        id: cat.id,
        name: cat.categoryTitle || cat.title || String(cat.id),
      }))
    } catch (e) {
      console.error(e)
    }
  },
  methods: {
    async tryToFetchList(resetPage = true) {
      if (resetPage && !this.sortMode) {
        this.pagination.page = 1
      }
      this.loading = true
      try {
        const params = {}
        if (this.filterData.search) params.keyword = this.filterData.search
        if (this.filterData.status) params.status = this.filterData.status
        if (this.filterData.tagIds.length) params.tagIds = this.filterData.tagIds
        if (this.filterData.categoryIds.length) params.categoryIds = this.filterData.categoryIds

        // 一般模式走後端分頁；排序模式保留原本完整列表行為
        if (!this.sortMode) {
          params.page = this.pagination.page
          params.perPage = this.pagination.pageSize
        }

        const res = await getHotelGoodsList(params)
        const items = Array.isArray(res) ? res : res?.items || []
        const pagination = res?.pagination || {}
        const serverTotal = Number(pagination.total)
        const hasServerPagination = !Array.isArray(res) && Object.prototype.hasOwnProperty.call(pagination, 'total')

        this.tableData = items
        this.useServerPagination = hasServerPagination
        this.listTotal = hasServerPagination && Number.isFinite(serverTotal) ? serverTotal : items.length
        this.sortDirty = false
      } catch (error) {
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    async tryToFetchTagList() {
      try {
        const tagsRes = await getHotelGoodsTags()
        this.tagList = Array.isArray(tagsRes) ? tagsRes : tagsRes?.items || []
      } catch (error) {
        console.error(error)
      }
    },
    // 模糊搜尋：debounce 800ms 後送出
    onSearchInput: debounce(function() {
      this.tryToFetchList()
    }, 800),
    onFilterChange() {
      this.tryToFetchList()
    },
    onFilterReset() {
      this.filterData = { search: '', status: '', tagIds: [], categoryIds: [] }
      this.tryToFetchList()
    },
    onPageChange(page) {
      this.pagination.page = page
      this.tryToFetchList(false)
    },
    handleSelectionChange(selected) {
      this.selectedIds = selected.map(({ id }) => id)
    },
    async deleteSelected() {
      if (!this.selectedIds.length) return
      try {
        await messageBoxDelete('選取的加購商品')
        await Promise.all(this.selectedIds.map((id) => deleteHotelGoods(id)))
        messageDelete()
        this.selectedIds = []
        this.tryToFetchList()
      } catch (error) {
        if (error !== 'cancel') console.error(error)
      }
    },
    // 直接在列表切換狀態（樂觀更新，失敗時還原）
    async onToggleStatus(row) {
      const prevStatus = row.status
      const newStatus = prevStatus === 'active' ? 'inactive' : 'active'
      row.status = newStatus
      try {
        await updateHotelGoods(row.id, { status: newStatus })
      } catch (error) {
        row.status = prevStatus
        console.error(error)
      }
    },
    openCreate() {
      this.$router.push({ name: 'hotel-addon-add' })
    },
    openEdit(row, column, event) {
      // 點到 checkbox、switch 或拖曳 handle 時不開啟頁面
      const el = event?.target
      if (el?.closest('.el-checkbox') || el?.closest('.el-switch') || el?.closest('.drag-handle')) {
        return
      }
      this.$router.push({ name: 'hotel-addon-edit', params: { goodsId: row.id } })
    },
    // 拖曳結束後更新本地排序，並顯示「儲存排序」按鈕
    endDraggable({ list }) {
      this.tableData = list
      this.sortDirty = true
    },
    // 批次送出排序陣列 goodsIds（越前 sort 越大）
    async saveSort() {
      this.sortSaving = true
      try {
        const goodsIds = this.tableData.map((item) => item.id)
        await sortHotelGoods(goodsIds)
        this.sortDirty = false
        this.$message({ message: '排序已儲存', type: 'success' })
        this.exitSortMode()
      } catch (error) {
        console.error(error)
      } finally {
        this.sortSaving = false
      }
    },
    enterSortMode() {
      this.filterData = { search: '', status: '', tagIds: [], categoryIds: [] }
      this.sortMode = true
      this.tryToFetchList()
    },
    exitSortMode() {
      this.sortMode = false
      this.sortDirty = false
    },
    onTagManageClosed() {
      this.tagManageVisible = false
      // 重新拉標籤清單，同步篩選器選項
      this.tryToFetchTagList()
    },
    // 將 categoryIds 轉為名稱陣列（透過 categoryMap）
    getCategoryNames(ids) {
      if (!ids || !ids.length) return []
      return ids.map((id) => this.getCategoryLabel(id))
    },
    // 解析分類顯示文字（支援 id 或物件）
    getCategoryLabel(category) {
      if (category && typeof category === 'object') {
        return category.categoryTitle || category.title || '—'
      }
      if (category == null || category === '') return '—'
      const key = String(category)
      return this.categoryMap[key] || key
    },
    // priceMode 顯示文字
    priceModeLabel(mode) {
      return mode === 'per_category' ? '規則' : '統一'
    },
    formatCurrency(value) {
      if (value == null || value === '') return '—'
      const n = Number(value)
      if (!Number.isFinite(n)) return `${this.currencySymbol}${value}`
      const hasDecimal = Math.abs(n % 1) > 0
      return `${this.currencySymbol}${n.toLocaleString('en-US', {
        minimumFractionDigits: hasDecimal ? 2 : 0,
        maximumFractionDigits: 2,
      })}`
    },
    // 取得商品的價格範圍文字
    getPriceRange(prices) {
      if (!prices || !prices.length) return '—'
      const values = prices.map((p) => Number(p.price)).filter((v) => Number.isFinite(v))
      if (!values.length) return '—'
      const min = Math.min(...values)
      const max = Math.max(...values)
      return min === max ? this.formatCurrency(min) : `${this.formatCurrency(min)} ~ ${this.formatCurrency(max)}`
    },
  },
}
</script>

<template>
  <div :class="$style.wrapper">
    <!-- 頂部工具列 -->
    <div :class="$style.toolbar">
      <div :class="$style.toolbarLeft">
        <el-input v-model="filterData.search" clearable placeholder="搜尋商品名稱" :class="$style.searchInput" @input="onSearchInput" @clear="tryToFetchList">
          <i slot="prefix" class="el-input__icon el-icon-search" />
        </el-input>
      </div>

      <div :class="$style.toolbarRight">
        <!-- 排序模式下的操作按鈕 -->
        <template v-if="sortMode">
          <transition name="el-fade-in">
            <el-button v-if="sortDirty" class="is-round" type="primary" :loading="sortSaving" @click="saveSort">
              <BaseIcon name="save" />
              <span>儲存排序</span>
            </el-button>
          </transition>
          <el-button class="is-round" @click="exitSortMode">
            <BaseIcon name="times" />
            <span>離開排序模式</span>
          </el-button>
        </template>

        <!-- 一般模式下的操作按鈕 -->
        <template v-else>
          <el-button class="is-round" @click="enterSortMode">
            <BaseIcon icon-style="fas" name="fa-grip-dots-vertical" />
            <span>排序模式</span>
          </el-button>

          <el-button class="is-round" @click="tagManageVisible = true">
            <BaseIcon name="tag" />
            <span>標籤管理</span>
          </el-button>

          <transition name="el-fade-in">
            <el-button v-if="selectedIds.length" class="is-round" :class="$style.deleteBtn" @click="deleteSelected">
              <BaseIcon name="trash-alt" />
              <span>刪除選取（{{ selectedIds.length }}）</span>
            </el-button>
          </transition>

          <el-button type="primary" class="is-round is-add" @click="openCreate">
            <BaseIcon name="plus" />
            <span>新增商品</span>
          </el-button>
        </template>
      </div>
    </div>

    <!-- 篩選列 -->
    <div :class="$style.filterBar">
      <el-select v-model="filterData.status" placeholder="所有狀態" clearable :disabled="sortMode" :class="$style.filterItem" @change="onFilterChange">
        <el-option label="啟用中" value="active" />
        <el-option label="已停用" value="inactive" />
      </el-select>

      <el-select v-model="filterData.categoryIds" multiple collapse-tags placeholder="篩選分類" :disabled="sortMode" :class="$style.filterItem" @change="onFilterChange">
        <el-option v-for="category in categoryOptions" :key="category.id" :label="category.name" :value="category.id" />
      </el-select>

      <el-select v-model="filterData.tagIds" multiple collapse-tags placeholder="篩選標籤" :disabled="sortMode" :class="$style.filterItem" @change="onFilterChange">
        <el-option v-for="tag in tagList" :key="tag.id" :label="tag.name" :value="tag.id">
          <el-tag :type="tag.color || undefined" size="mini" style="margin-right: 6px;">
            {{ tag.name }}
          </el-tag>
        </el-option>
      </el-select>

      <el-button v-if="filterData.status || filterData.tagIds.length || filterData.categoryIds.length" type="text" class="text-primary" @click="onFilterReset">
        重置篩選
      </el-button>

      <span v-if="sortMode" :class="$style.sortModeHint">
        <BaseIcon name="info-circle" />
        排序模式：拖曳列表調整順序，儲存後生效
      </span>
    </div>

    <!-- 資料表 -->
    <div class="el-table-wrapper">
      <ElTableDraggable :key="canDrag ? 'drag' : 'nodrag'" handle=".drag-handle" @drop="endDraggable">
        <el-table
          ref="table"
          v-loading="loading"
          :data="pagedTableData"
          element-loading-text="載入中..."
          :class="[$style.table, sortMode && $style.tableSortMode]"
          @row-click="openEdit"
          @selection-change="handleSelectionChange"
        >
          <!-- Checkbox -->
          <el-table-column type="selection" width="45" />

          <!-- 封面圖 -->
          <el-table-column label="封面圖" width="76">
            <template v-slot="{ row }">
              <el-image :src="row.image || ''" fit="cover" :class="$style.thumbnail">
                <div slot="error" :class="$style.thumbnailError">
                  <BaseIcon name="image" />
                </div>
              </el-image>
            </template>
          </el-table-column>

          <!-- 展開行：prices 明細 -->
          <el-table-column type="expand">
            <template v-slot="{ row }">
              <div :class="$style.expandPrices">
                <template v-if="row.prices && row.prices.length">
                  <PriceSubTable :row="row" :format-currency="formatCurrency" />
                </template>
                <span v-else :class="$style.emptyText" style="font-size: 13px;">暫無價格資料</span>
              </div>
            </template>
          </el-table-column>

          <!-- 商品名稱 -->
          <el-table-column label="商品名稱" prop="name" min-width="120" />

          <!-- 適用大分類 -->
          <el-table-column label="分類" min-width="100">
            <template v-slot="{ row }">
              <div :class="$style.tagList">
                <el-tag v-for="name in getCategoryNames(row.categoryIds)" :key="name" size="mini" type="info" :class="$style.tag">{{ name }}</el-tag>
                <span v-if="!row.categoryIds || !row.categoryIds.length" :class="$style.emptyText">—</span>
              </div>
            </template>
          </el-table-column>

          <!-- 計價方式 -->
          <el-table-column label="計價" width="60">
            <template v-slot="{ row }">
              <span>{{ priceModeLabel(row.priceMode) }}</span>
            </template>
          </el-table-column>

          <!-- 適用分店 -->
          <el-table-column label="適用" width="60">
            <template v-slot="{ row }">
              <span v-if="row.productIds && row.productIds.length">{{ row.productIds.length }} 間</span>
              <span v-else :class="$style.emptyText">—</span>
            </template>
          </el-table-column>

          <!-- 標籤 -->
          <el-table-column label="標籤" min-width="80">
            <template v-slot="{ row }">
              <div :class="$style.tagList">
                <el-tag v-for="tag in row.tags" :key="tag.id" :type="tag.color || undefined" size="mini" :class="$style.tag">
                  {{ tag.name }}
                </el-tag>
                <span v-if="!row.tags || !row.tags.length" :class="$style.emptyText">—</span>
              </div>
            </template>
          </el-table-column>

          <!-- 價格 -->
          <el-table-column label="價格" min-width="140">
            <template v-slot="{ row }">
              <span :class="$style.priceText">{{ getPriceRange(row.prices) }}</span>
            </template>
          </el-table-column>

          <!-- 狀態 -->
          <el-table-column label="狀態" width="80">
            <template v-slot="{ row }">
              <el-switch :value="row.status === 'active'" :active-color="$style.colorPrimary" inactive-color="#c0c4cc" @change="onToggleStatus(row)" @click.native.stop />
            </template>
          </el-table-column>

          <!-- 拖曳排序 Handle（有篩選條件時隱藏） -->
          <el-table-column v-if="canDrag" width="50" align="center">
            <template #header>
              <el-tooltip content="拖曳改變排序" placement="top">
                <BaseIcon icon-style="fas" name="fa-grip-dots-vertical" style="color: #c0c4cc;" />
              </el-tooltip>
            </template>
            <template #default>
              <el-button type="text" class="drag-handle" :class="$style.dragBtn" @click.stop>
                <BaseIcon icon-style="fas" name="fa-grip-dots-vertical" />
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </ElTableDraggable>
    </div>

    <!-- 分頁器（排序模式下隱藏） -->
    <div v-if="!sortMode && totalItems > pagination.pageSize" :class="$style.paginationBar">
      <el-pagination :current-page.sync="pagination.page" :page-size="pagination.pageSize" :total="totalItems" layout="total, prev, pager, next" background @current-change="onPageChange" />
    </div>

    <!-- 標籤管理 Dialog -->
    <GoodsTagManage :dialog-visible.sync="tagManageVisible" @closed="onTagManageClosed" />
  </div>
</template>

<style lang="scss" module>

.wrapper {
  padding-bottom: 40px;
}

// ── 工具列 ──────────────────────────────────────────────────
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 20px 8px;
}

.toolbarLeft {
  flex: 1;
  min-width: 200px;
  max-width: 320px;
}

.toolbarRight {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.searchInput {
  width: 100%;
}

// ── 篩選列 ──────────────────────────────────────────────────
.filterBar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 8px 20px 4px;
}

.filterItem {
  width: 160px;
}

.sortModeHint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #909399;
  padding: 4px 8px;
  background: #f5f7fa;
  border-radius: 4px;
}

// ── 刪除按鈕 ─────────────────────────────────────────────────
.deleteBtn {
  background-color: #f06c6c;
  color: white;
  border-color: #f06c6c;

  &:hover,
  &:focus {
    background-color: #ff8080;
    color: white;
    border-color: #ff8080;
  }
}

// ── 表格 ─────────────────────────────────────────────────────
.table {
  @include layer-elevation(5);
  cursor: pointer;
}

.tableSortMode {
  outline: 2px dashed $color-primary;
  outline-offset: -2px;
}

.thumbnail {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  object-fit: cover;
}

.thumbnailError {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: #f5f5f5;
  border-radius: 6px;
  color: #c0c4cc;
}

.tagList {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.tag {
  margin: 0;
}

.emptyText {
  color: #c0c4cc;
}

.priceText {
  font-weight: 500;
  color: #333;
}

.dragBtn {
  cursor: grab;

  span svg {
    color: $color-primary;
  }

  &:active {
    cursor: grabbing;
  }
}

// ── 分頁列 ──────────────────────────────────────────────────
.paginationBar {
  display: flex;
  justify-content: flex-end;
  padding: 16px 20px 0;
}
</style>
